// The single injection point for the two tax engines. The default provider is the real @divorceos/tax-us /
// @divorceos/tax-ca packages; tests use a fake.
import type { CaTaxInput, CaTaxResult, FederalTaxInput, FederalTaxResult, IrsStatus, TaxYear } from '@divorceos/schema';
import { computeFederalTax, federalWithholding } from '@divorceos/tax-us';
import { computeCaTax, caWithholding } from '@divorceos/tax-ca';

export interface W4Facts { dependentsCredit: number; deductions: number; otherIncome: number }

export interface TaxProvider {
  federal(input: FederalTaxInput): FederalTaxResult;
  state(input: CaTaxInput): CaTaxResult;
  /** Annual federal withholding for annual wages (Pub 15-T). */
  federalWithholding(year: TaxYear, status: IrsStatus, annualWages: number, w4: W4Facts): number;
  /** Annual CA withholding for annual wages and n allowances (DE 44 Method B). */
  stateWithholding(year: TaxYear, status: IrsStatus, annualWages: number, allowances: number): number;
}

/**
 * Compatibility switches that make the engine reproduce the ORIGINAL Xspouse (2023 demo build) where the
 * original is known to differ from the published tax law (see notes/ca_tax.md §10, notes/us_tax.md §9):
 *  - `xspouseTables`: ca_tax.dll's 2023/2024 California figures are the 2022 constants x 1.069 / x 1.1128
 *    (FTB indexed 3.1 % / 3.3 %), its CalEITC is the unindexed 2022 table, YCTC/FYTC stay at 1,083, the 2024
 *    SDI keeps a wage cap; us_tax.dll caps the ACTC at 1,500 per child for every year and projects 2024 as
 *    2023 x 1.032. ON by default so results match the original; switch off for the FTB/IRS-published tables.
 */
export interface EngineCompat {
  xspouseTables?: boolean;
}

export const DEFAULT_COMPAT: Required<EngineCompat> = { xspouseTables: true };

/** A provider bound to the real tax packages with the given compat switches. */
export function taxProviderFor(compat: EngineCompat = {}): TaxProvider {
  const xs = compat.xspouseTables ?? DEFAULT_COMPAT.xspouseTables;
  const us = { tables: xs ? 'xspouse' as const : 'irs' as const };
  const ca = { tables: xs ? 'xspouse' as const : 'ftb' as const };
  return {
    federal: (i) => computeFederalTax(i, us),
    state: (i) => computeCaTax(i, ca),
    federalWithholding: (y, s, w, w4) => federalWithholding(y, s, w, w4, us.tables),
    stateWithholding: (y, s, w, n) => caWithholding(y, s, w, n, ca),
  };
}

/** The default provider: real tax engines in compat mode (matches the original Xspouse tables). */
export const defaultTaxProvider: TaxProvider = taxProviderFor(DEFAULT_COMPAT);
/** Real tax engines with the published FTB / IRS tables (compat off). */
export const publishedTablesProvider: TaxProvider = taxProviderFor({ xspouseTables: false });

/** Options accepted by computeCase / settlementTable / timesharePositions. */
export interface EngineOptions {
  provider?: TaxProvider;
  /** "Now" for the CPI rule of the low-income threshold (§2.3); defaults to the wall clock. */
  now?: Date;
  /** Compatibility switches (ignored when `provider` is given). Defaults to matching the original Xspouse. */
  compat?: EngineCompat;
}

/** The provider an options object selects: explicit provider, else the real engines with the compat switches. */
export function providerOf(opts: EngineOptions = {}): TaxProvider {
  if (opts.provider) return opts.provider;
  if (!opts.compat) return defaultTaxProvider;
  return taxProviderFor(opts.compat);
}
